import { useLayoutEffect, useRef, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';

import Headers from './Headers';
import ProgressiveScroller from './ProgressiveScroller';
import useScrollListener, { ScrollDirection } from './UseScrollListener';
import StaticRenderer from './StaticRenderer';
import StatusTextContainer from './StatusTextContainer';
import useIsPrinting from '../../../utilities/PrintUtilities';
import {
    useContentWidth,
    useRenderLayoutParams,
    useRowHeight,
    useContentHeight,
    useViewportWidth,
    useViewportHeight,
} from './RenderContext';
import { useDataStore } from '../DataStore';
import useThrottled from '../../../utilities/UseThrottled';
import useSingletonValue from '../../../utilities/UseSingletonValue';

const useStyles = makeStyles((theme) => ({
    viewRoot: {
        overflowX: 'hidden',
        overflowY: 'hidden',
        minHeight: 46,

        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),
    },
}));

export default function View(props) {
    const { showHeaders, statusComponent, viewportRef, onScroll } = props;

    const isPrinting = useIsPrinting();
    const headerScrollerRef = useRef();
    const classes = useStyles(props);

    // quickly synchronize the horizontal scroll without requiring a re-render
    useFastHeaderSync(headerScrollerRef, viewportRef);

    // onScroll comes in at the toplevel DataTable. Used by the ReportingDataTable
    // to stop auto-refresh when we scroll down
    useScrollListener(ScrollDirection.Top, viewportRef, onScroll);

    return (
        <div className={classes.viewRoot}>
            {showHeaders && <Headers scrollerRef={headerScrollerRef} />}

            <VerticalScrollContainer
                viewportRef={viewportRef}
                scrollerCmp={isPrinting ? StaticRenderer : ProgressiveScroller}
            >
                <StatusTextContainer>{statusComponent}</StatusTextContainer>
            </VerticalScrollContainer>
        </div>
    );
}

const useScrollContainerStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,

        position: 'relative',

        '&$fixedHeightLayout': {
            display: 'block',
            height: 'auto',
            minHeight: 'auto',
        },

        '&$autoHeightLayout': {
            ...theme.mixins.flex.grow(1),
            ...theme.mixins.flex.shrink(1),
            minHeight: 0,
            height: 0,
        },

        '@media print': {
            display: 'block',
            position: 'static',
        },
    },

    scroller: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),

        overflowX: 'auto',
        overflowY: 'auto',
        lineHeight: 0,

        '&$horizontalFit': {
            overflowX: 'hidden',
        },

        '&$verticalFit': {
            overflowY: 'hidden',
        },
    },

    fixedHeightLayout: {},
    autoHeightLayout: {},
    horizontalFit: {},
    verticalFit: {},
}));

function VerticalScrollContainer(props) {
    const { children, viewportRef, scrollerCmp: Scroller } = props;

    const classes = useScrollContainerStyles();
    const scrollerStyles = {};
    const scrollerClasses = [];
    const scrollbarHeight = useScrollbarHeight(viewportRef?.current);

    const rowHeight = useRowHeight();
    const { hasLoaded } = useDataStore();
    const contentWidth = useContentWidth();
    const contentHeight = useContentHeight();
    const viewportWidth = useViewportWidth();
    const viewportHeight = useViewportHeight();
    const { scrollerHeight, visibleRows } = useRenderLayoutParams();

    if (scrollerHeight !== null) {
        let height = scrollerHeight;

        // add an extra bit of height if we have a horizontal scrollbar
        if (contentWidth > viewportWidth) {
            height += scrollbarHeight;
        }

        scrollerStyles.height = height;
    }

    const layoutClassName = visibleRows === 0 ? classes.autoHeightLayout : classes.fixedHeightLayout;

    if (contentWidth <= viewportWidth || !hasLoaded) {
        scrollerClasses.push(classes.horizontalFit);
    }

    if (contentHeight <= viewportHeight) {
        scrollerClasses.push(classes.verticalFit);
    }

    const minHeightStyle = {
        minHeight: rowHeight,
    };

    return (
        <div
            className={clsx(classes.root, layoutClassName)}
            style={viewportHeight >= contentHeight ? minHeightStyle : {}}
        >
            <div ref={viewportRef} className={clsx(classes.scroller, ...scrollerClasses)} style={scrollerStyles}>
                <Scroller />
            </div>

            {children}
        </div>
    );
}

function useFastHeaderSync(headerScrollerRef, bodyScrollerRef) {
    useScrollListener(ScrollDirection.Left, bodyScrollerRef, (leftPos) => {
        headerScrollerRef.current.style.left = `${-leftPos}px`;
    });
}

function getScrollbarHeight(el) {
    if (!el) return 0;

    return el.offsetHeight - el.clientHeight;
}

function useScrollbarHeight(el) {
    const [scrollbarHeight, _setScrollbarHeight] = useState(0);
    const currentScrollbarHeight = getScrollbarHeight(el);

    const throttleSettings = useSingletonValue(
        /** @type {() => import('lodash').ThrottleSettings} */ () => ({
            leading: true,
            trailing: false,
        })
    );

    const setScrollbarHeight = useThrottled(_setScrollbarHeight, 100, throttleSettings);

    useLayoutEffect(() => {
        // We only want to mess with scrollbar heights if we do something to change them. At first, we don't know the height of the scrollbar, but if we zoom,
        // that can change the height too. Chrome also seems to have (what I think to be a bug) behavior where at times the scrollbar will oscillate between 16
        // and 17 pixels, and React will kill off our render cycle. Here we just only change the value if it really matters (i.e., a change of > 4 pixels)
        if (
            scrollbarHeight !== currentScrollbarHeight &&
            currentScrollbarHeight > 0 &&
            Math.abs(scrollbarHeight - currentScrollbarHeight) > 4
        ) {
            setScrollbarHeight(currentScrollbarHeight);
        }
    }, [currentScrollbarHeight, scrollbarHeight, setScrollbarHeight]);

    return scrollbarHeight;
}
